import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth, can } from "@/lib/auth";
import { LanguageProvider } from "@/lib/i18n";
import Sidebar from "@/components/Sidebar";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Transactions from "@/pages/Transactions";
import BankAccounts from "@/pages/BankAccounts";
import Contacts from "@/pages/Contacts";
import Payments from "@/pages/Payments";
import Reports from "@/pages/Reports";
import Stock from "@/pages/Stock";
import Orders from "@/pages/Orders";
import CustomerNotes from "@/pages/CustomerNotes";
import ProductList from "@/pages/ProductList";
import Cost from "@/pages/Cost";
import Assistant from "@/pages/Assistant";
import DataUpdate from "@/pages/DataUpdate";
import PersonnelManagement from "@/pages/PersonnelManagement";
import AuditLog from "@/pages/AuditLog";

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

const PERM_ROUTE = { dashboard: "/", orders: "/siparis", stock: "/stok", notes: "/notlar", payments: "/odemeler" };

function firstAllowedPath(user) {
  if (can(user, "dashboard")) return "/";
  for (const p of (user?.permissions || [])) if (PERM_ROUTE[p]) return PERM_ROUTE[p];
  return "/siparis";
}

function Perm({ need, children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (can(user, need)) return children;
  return <Navigate to={firstAllowedPath(user)} replace />;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === "admin") return children;
  return <Navigate to={firstAllowedPath(user)} replace />;
}

function AppLayout() {
  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 lg:ml-64 min-w-0">
        <Routes>
          <Route path="/" element={<Perm need="dashboard"><Dashboard /></Perm>} />
          <Route path="/hareketler" element={<Perm need="transactions"><Transactions /></Perm>} />
          <Route path="/hesaplar" element={<Perm need="accounts"><BankAccounts /></Perm>} />
          <Route path="/cari" element={<Perm need="contacts"><Contacts /></Perm>} />
          <Route path="/musteriler" element={<Perm need="customers"><Contacts fixedType="customer" /></Perm>} />
          <Route path="/tedarikciler" element={<Perm need="suppliers"><Contacts fixedType="supplier" /></Perm>} />
          <Route path="/notlar" element={<Perm need="notes"><CustomerNotes /></Perm>} />
          <Route path="/stok" element={<Perm need="stock"><Stock /></Perm>} />
          <Route path="/urunler" element={<Perm need="products"><ProductList /></Perm>} />
          <Route path="/siparis" element={<Perm need="orders"><Orders /></Perm>} />
          <Route path="/maliyet" element={<Perm need="cost"><Cost /></Perm>} />
          <Route path="/odemeler" element={<Perm need="payments"><Payments /></Perm>} />
          <Route path="/raporlar" element={<Perm need="reports"><Reports /></Perm>} />
          <Route path="/asistan" element={<Perm need="assistant"><Assistant /></Perm>} />
          <Route path="/veri-guncelle" element={<Perm need="import"><DataUpdate /></Perm>} />
          <Route path="/personel" element={<AdminOnly><PersonnelManagement /></AdminOnly>} />
          <Route path="/islem-logu" element={<AdminOnly><AuditLog /></AdminOnly>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
            <Route path="/sifre-sifirla" element={<ResetPassword />} />
            <Route
              path="/*"
              element={
                <Protected>
                  <AppLayout />
                </Protected>
              }
            />
          </Routes>
          <Toaster theme="dark" position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
